import { useEffect, useMemo, useState } from "react";
import "./App.css";
import menuDefinitions from "./menuDefinitions.json";

const API_BASE = (process.env.REACT_APP_API_URL || "/api").replace(/\/+$/, "");
const ADMIN_TOKEN_KEY = "food-delivery-admin-token";
const CUSTOMER_ORDER_IDS_KEY = "food-delivery-customer-order-ids";
const CUSTOMER_DEVICE_ID_KEY = "food-delivery-customer-device-id";
const LOCALHOST_API_FALLBACK = "http://127.0.0.1:4000/api";
const MENU_PLACEHOLDER_IMAGE = "/meal-placeholder.svg";
const DISH_IMAGE_BY_ID = {
  "strogonoff-de-frango": "/meals/strogonoff-de-frango.jpg",
  "bacalhau-a-gomes-de-sa": "/meals/bacalhau-a-gomes-de-sa.jpg",
  "lombinhos-de-porco": "/meals/lombinhos-de-porco.jpg",
  "peixe-no-forno": "/meals/peixe-no-forno.jpg",
  "arroz-de-pato": "/meals/arroz-de-pato.jpg",
  "bifana": "/meals/bifana.jpg",
  "almondegas-com-pure": "/meals/almondegas-com-pure.jpg",
  "bobo-de-frango": "/meals/bobo-de-frango.jpg",
  "caldeirada-de-peixe": "/meals/caldeirada-de-peixe.jpg",
  "filete-de-peixe-com-salada-russa": "/meal-photos/filete-de-peixe-com-salada-russa.png",
  "bacalhau-a-chefe": "/meal-photos/bacalhau-a-chefe.jpg",
  "bacalhau-gratinado": "/meal-photos/bacalhau-gratinado.jpeg",
  "carne-de-porco-a-portuguesa": "/meal-photos/carne-de-porco-a-portuguesa.jpeg",
  "lombinhos-de-porco-com-arroz-primavera": "/meal-photos/lombinhos-de-porco-com-arroz-primavera.heic",
  "salada-de-quinoa-com-peito-de-frango-grelhado": "/meal-photos/salada-de-quinoa-com-peito-de-frango-grelhado.jpeg",
  "salada-de-rosbife-com-tabul": "/meal-photos/salada-de-rosbife-com-tabul.heic",
  "vitela-estufada-com-arroz": "/meal-photos/vitela-estufada-com-arroz.jpeg",
  "costeletas-com-castanhas": "/meals/costeletas-com-castanhas.jpg",
  "feijao-frade-com-atum": "/meals/feijao-frade-com-atum.jpg",
  "pasteis-de-bacalhau": "/meals/pasteis-de-bacalhau.jpg",
  "frango-com-massa": "/meals/frango-com-massa.jpg",
  "calamares": "/meals/calamares.jpg",
  "carrot-rice": "/meals/carrot-rice.png",

};

const WEEK_DAYS = menuDefinitions.weekDays;
const WEEKLY_MEAL_SLOTS = menuDefinitions.weeklyMealSlots;
const DISH_POOL = menuDefinitions.dishPool;
const DEFAULT_WEEKLY_PLAN = menuDefinitions.defaultWeeklyPlan;
const DEFAULT_FEATURED_DISH_IDS = DISH_POOL.slice(0, 6)
  .map((dish) => dish.id)
  .filter(Boolean);
const ORDER_CUTOFF_HOUR = 11;
const NEXT_DAY_ORDER_START_HOUR = 14;
const ORDER_TIME_ZONE = "Europe/Lisbon";
const DAILY_ORDER_LIMIT = Number(process.env.REACT_APP_DAILY_ORDER_LIMIT) || 20;

const emptyForm = {
  customerName: "",
  phone: "",
  address: "",
  quantity: 1,
  extraNotes: "",
};

const emptyLoginForm = {
  username: "",
  password: "",
};

const emptyCustomCardDraft = {
  day: "",
  dishName: "",
  description: "",
  imageUrl: "",
  price: "",
  lactoseFree: false,
  glutenFree: false,
  vegan: false,
  enabled: true,
};
const emptyCustomerWarning = {
  enabled: false,
  message: "Serviço encerrado dia ---",
  date: "",
};
const EXTRA_NOTES_MAX_LENGTH = 200;

const MEAT_KEYWORDS = [
  "carne",
  "frango",
  "porco",
  "pato",
  "bifana",
  "almôndegas",
  "lombinhos",
  "rosbife",
  "vitela",
  "peru",
  "costeletas",
];
const FISH_KEYWORDS = [
  "peixe",
  "bacalhau",
  "filete",
  "atum",
  "pastéis",
  "caldeirada",
];

function getOrderStatus(order) {
  if (order.isDelivered && order.isPaid) {
    return {
      label: "Concluído",
      tone: "completed",
      actionLabel: null,
      actionType: null,
    };
  }

  if (order.isDelivered) {
    return {
      label: "Pagamento",
      tone: "payment",
      actionLabel: "Confirmar pagamento",
      actionType: "payment",
    };
  }

  return {
    label: "Entrega",
    tone: "delivery",
    actionLabel: "Confirmar entrega",
    actionType: "delivery",
  };
}

function normalizeDayPlan(dayPlan) {
  if (typeof dayPlan === "string") {
    return {
      meat: dayPlan,
      fish: "",
      vegan: "",
    };
  }

  return {
    meat: String(dayPlan?.meat || "").trim(),
    fish: String(dayPlan?.fish || "").trim(),
    vegan: String(dayPlan?.vegan || "").trim(),
  };
}

function normalizeWeeklyPlan(plan) {
  return WEEK_DAYS.reduce((acc, day) => {
    acc[day] = normalizeDayPlan(plan?.[day]);
    return acc;
  }, {});
}

function buildMenus(weeklyPlan) {
  return WEEK_DAYS.flatMap((day) => {
    const dayPlan = normalizeDayPlan(weeklyPlan[day]);

    return WEEKLY_MEAL_SLOTS.map((slot) => {
      const dishId = dayPlan[slot.id];
      const dish = DISH_POOL.find((item) => item.id === dishId);

      if (!dish) {
        return null;
      }

      return {
        id: `${day}-${slot.id}-${dish.id}`,
        day,
        dishId: dish.id,
        mealType: slot.id,
        mealTypeLabel: slot.label,
        name: dish.name,
        price: dish.price,
        description: dish.description,
        dairyFree: Boolean(dish.dairyFree),
        glutenFree: Boolean(dish.glutenFree),
        vegan: slot.id === "vegan" || Boolean(dish.vegan),
        imageUrl: DISH_IMAGE_BY_ID[dish.id] || "",
        sourceType: "weekly",
      };
    }).filter(Boolean);
  });
}

function normalizeImageUrl(value) {
  const raw = String(value || "").trim();

  if (!raw) {
    return "";
  }

  if (raw.startsWith("//")) {
    return `https:${raw}`;
  }

  if (raw.startsWith("/")) {
    return raw;
  }

  try {
    const parsed = new URL(raw);

    // Support pasted Google Images result links by extracting the direct URL.
    if (parsed.hostname.includes("google.") && parsed.pathname === "/imgres") {
      const nested = parsed.searchParams.get("imgurl");
      if (nested) {
        return nested;
      }
    }

    return parsed.toString();
  } catch {
    return raw;
  }
}

function buildCustomMenus(customCards) {
  return (customCards || [])
    .map((card) => {
      const parsedPrice = Number(card.price);
      const safePrice =
        Number.isFinite(parsedPrice) && parsedPrice > 0 ? parsedPrice : null;

      if (
        !card.enabled ||
        !String(card.day || "").trim() ||
        !String(card.dishName || "").trim() ||
        !safePrice
      ) {
        return null;
      }

      return {
        id: `custom-${card.id}`,
        day: card.day.trim(),
        dishId: `custom-dish-${card.id}`,
        name: card.dishName.trim(),
        price: safePrice,
        description: String(card.description || "").trim(),
        dairyFree: Boolean(card.dairyFree ?? card.lactoseFree),
        glutenFree: Boolean(card.glutenFree),
        vegan: Boolean(card.vegan),
        imageUrl: normalizeImageUrl(card.imageUrl),
        sourceType: "custom",
      };
    })
    .filter(Boolean);
}

function getDishById(dishId) {
  return DISH_POOL.find((dish) => dish.id === dishId) || null;
}

function getDishCategoryLabel(dish) {
  const searchableText = `${dish?.name || ""} ${dish?.description || ""}`.toLowerCase();

  if (dish?.vegan) {
    return "Vegano";
  }

  if (FISH_KEYWORDS.some((keyword) => searchableText.includes(keyword))) {
    return "Peixe";
  }

  if (MEAT_KEYWORDS.some((keyword) => searchableText.includes(keyword))) {
    return "Carne";
  }

  return "Outro";
}

function getDishDietaryLabels(dish) {
  const labels = [];

  if (dish?.vegan) {
    labels.push("Vegano");
  }

  if (dish?.dairyFree) {
    labels.push("Sem lactose");
  }

  if (dish?.glutenFree) {
    labels.push("Sem glúten");
  }

  return labels;
}

function getDishSearchText(dish) {
  return [
    dish?.name,
    dish?.description,
    dish?.id,
    getDishCategoryLabel(dish),
    ...getDishDietaryLabels(dish),
  ]
    .join(" ")
    .toLowerCase();
}

function formatMenuName(menu) {
  const tags = [];
  const isVegan = Boolean(menu?.vegan || menu?.mealType === "vegan");
  const isDairyFree = Boolean(menu?.dairyFree ?? menu?.lactoseFree);
  const isGlutenFree = Boolean(menu?.glutenFree);

  if (isVegan) {
    tags.push("🌿");
  }

  if (isDairyFree && isGlutenFree) {
    tags.push("🍶🌽❌");
  } else if (isDairyFree) {
    tags.push("🍶❌");
  } else if (isGlutenFree) {
    tags.push("🌽❌");
  }

  return tags.length > 0 ? `${menu.name} ${tags.join(" ")}` : menu.name;
}

function formatCsvCell(value) {
  const text = String(value ?? "");
  const escaped = text.replaceAll("\"", "\"\"");
  return `"${escaped}"`;
}

function formatExportDateTime(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat("pt-PT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(date);
}

function getOrderHour(date = new Date()) {
  const hour = new Intl.DateTimeFormat("en-GB", {
    timeZone: ORDER_TIME_ZONE,
    hour: "2-digit",
    hour12: false,
  }).format(date);

  return Number(hour);
}

function getOrderDateKey(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: ORDER_TIME_ZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).formatToParts(date);
  const partByType = Object.fromEntries(parts.map((part) => [part.type, part.value]));

  return `${partByType.year}-${partByType.month}-${partByType.day}`;
}

function addDaysToDateKey(dateKey, days) {
  const [year, month, day] = String(dateKey || "").split("-").map(Number);

  if (!year || !month || !day) {
    return "";
  }

  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function getWeekDayFromDateKey(dateKey) {
  const [year, month, day] = String(dateKey || "").split("-").map(Number);

  if (!year || !month || !day) {
    return "";
  }

  const date = new Date(Date.UTC(year, month - 1, day));
  const dayIndex = date.getUTCDay();
  return WEEK_DAYS[dayIndex === 0 ? 6 : dayIndex - 1] || "";
}

function getOrderTarget(value = new Date()) {
  const hour = getOrderHour(value);
  const todayKey = getOrderDateKey(value);

  if (hour < ORDER_CUTOFF_HOUR) {
    return {
      isOpen: true,
      orderDateKey: todayKey,
      day: getWeekDayFromDateKey(todayKey),
      label: "hoje",
      availableText: "Pedidos disponíveis para hoje",
      closedTitle: "",
      closedDescription: "",
    };
  }

  if (hour >= NEXT_DAY_ORDER_START_HOUR) {
    const tomorrowKey = addDaysToDateKey(todayKey, 1);

    return {
      isOpen: true,
      orderDateKey: tomorrowKey,
      day: getWeekDayFromDateKey(tomorrowKey),
      label: "amanhã",
      availableText: "Pedidos disponíveis para amanhã",
      closedTitle: "",
      closedDescription: "",
    };
  }

  const tomorrowKey = addDaysToDateKey(todayKey, 1);

  return {
    isOpen: false,
    orderDateKey: tomorrowKey,
    day: getWeekDayFromDateKey(tomorrowKey),
    label: "amanhã",
    availableText: "Pedidos disponíveis para amanhã",
    closedTitle: "Pedidos temporariamente encerrados.",
    closedDescription:
      "Os pedidos para hoje encerraram às 11:00. As encomendas para amanhã abrem às 14:00.",
  };
}

function getOrderTargetDateKey(order) {
  const explicitDateKey = String(order?.orderDateKey || "");

  if (/^\d{4}-\d{2}-\d{2}$/.test(explicitDateKey)) {
    return explicitDateKey;
  }

  return getOrderDateKey(order?.createdAt);
}

function normalizeCustomerWarning(warning = {}) {
  return {
    enabled: Boolean(warning.enabled),
    message: String(warning.message || emptyCustomerWarning.message).slice(0, 180),
    date: /^\d{4}-\d{2}-\d{2}$/.test(String(warning.date || ""))
      ? String(warning.date)
      : "",
    updatedAt: String(warning.updatedAt || ""),
  };
}

function normalizeOutsideOrderCounts(counts = {}) {
  if (!counts || typeof counts !== "object" || Array.isArray(counts)) {
    return {};
  }

  return Object.entries(counts).reduce((acc, [dateKey, count]) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateKey))) {
      return acc;
    }

    acc[dateKey] = Math.max(0, Math.floor(Number(count) || 0));
    return acc;
  }, {});
}

function formatWarningDate(dateKey) {
  const [year, month, day] = String(dateKey || "").split("-");

  if (!year || !month || !day) {
    return "";
  }

  return `${day}/${month}/${year}`;
}

function getDateParts(dateKey) {
  const [year, month, day] = String(dateKey || "").split("-");

  return {
    day: /^\d{2}$/.test(day || "") ? day : "",
    month: /^\d{2}$/.test(month || "") ? month : "",
    year: /^\d{4}$/.test(year || "") ? year : "",
  };
}

function getDaysInMonth(year, month) {
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return 31;
  }

  return new Date(year, month, 0).getDate();
}

function buildDateKeyFromParts({ day, month, year }) {
  if (!day || !month || !year) {
    return "";
  }

  const maxDay = getDaysInMonth(Number(year), Number(month));
  const safeDay = Math.min(Number(day), maxDay);

  return `${year}-${month}-${String(safeDay).padStart(2, "0")}`;
}

function getCustomerWarningDisplay(warning, todayKey) {
  const normalizedWarning = normalizeCustomerWarning(warning);
  const message = normalizedWarning.message.trim();

  if (!normalizedWarning.enabled || !message) {
    return { isVisible: false, message: "", dateLabel: "" };
  }

  if (!normalizedWarning.date) {
    return {
      isVisible: true,
      message: message.replaceAll("---", "").replace(/\sdia\s*$/i, "").trim(),
      dateLabel: "",
    };
  }

  const dateLabel =
    normalizedWarning.date === todayKey ? "hoje" : formatWarningDate(normalizedWarning.date);
  let displayMessage = message;

  if (message.includes("dia ---")) {
    displayMessage = message.replaceAll("dia ---", "").trim();
  } else if (message.includes("---")) {
    displayMessage = message.replaceAll("---", "").trim();
  }

  return {
    isVisible: true,
    message: displayMessage,
    dateLabel,
  };
}

async function requestJson(path, { method = "GET", token, body } = {}) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const requestInit = {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  };

  const execute = async (baseUrl) => {
    const response = await fetch(`${baseUrl}${normalizedPath}`, requestInit);
    const raw = await response.text();

    let data = null;

    try {
      data = raw ? JSON.parse(raw) : null;
    } catch {
      data = null;
    }

    return { response, raw, data };
  };

  let { response, raw, data } = await execute(API_BASE);

  const isLocalhost =
    typeof window !== "undefined" &&
    ["localhost", "127.0.0.1"].includes(window.location.hostname);

  const shouldRetryLocalFallback =
    isLocalhost &&
    API_BASE === "/api" &&
    response.status === 404 &&
    raw.includes("Cannot") &&
    raw.includes("/api/");

  if (shouldRetryLocalFallback) {
    ({ response, raw, data } = await execute(LOCALHOST_API_FALLBACK));
  }

  if (!response.ok) {
    throw new Error(
      data?.error || raw || `Request failed (${response.status}).`
    );
  }

  return data;
}

function App() {
  const [weeklyPlan, setWeeklyPlan] = useState(() => normalizeWeeklyPlan(DEFAULT_WEEKLY_PLAN));
  const [customCards, setCustomCards] = useState([]);
  const [featuredDishIds, setFeaturedDishIds] = useState(DEFAULT_FEATURED_DISH_IDS);
  const [orders, setOrders] = useState([]);
  const [outsideOrderCounts, setOutsideOrderCounts] = useState({});
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const [view, setView] = useState("menu");
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [pendingAction, setPendingAction] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [actionError, setActionError] = useState("");
  const [orderConfirmation, setOrderConfirmation] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginForm, setLoginForm] = useState(emptyLoginForm);
  const [customCardDraft, setCustomCardDraft] = useState(emptyCustomCardDraft);
  const [isAddingCustomCard, setIsAddingCustomCard] = useState(false);
  const [editingCustomCardId, setEditingCustomCardId] = useState("");
  const [pendingCustomCardDeleteId, setPendingCustomCardDeleteId] = useState(null);
  const [dishPicker, setDishPicker] = useState(null);
  const [dishPickerSearch, setDishPickerSearch] = useState("");
  const [customerWarning, setCustomerWarning] = useState(emptyCustomerWarning);
  const [customerWarningDraft, setCustomerWarningDraft] = useState(emptyCustomerWarning);
  const [pendingCleaningAction, setPendingCleaningAction] = useState("");
  const [pendingAdminView, setPendingAdminView] = useState("admin");
  const [adminToken, setAdminToken] = useState(
    () => window.localStorage.getItem(ADMIN_TOKEN_KEY) || ""
  );
  const [customerDeviceId] = useState(() => {
    const existing = window.localStorage.getItem(CUSTOMER_DEVICE_ID_KEY);
    if (existing) {
      return existing;
    }

    const generated =
      window.crypto?.randomUUID?.() ||
      `device-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    window.localStorage.setItem(CUSTOMER_DEVICE_ID_KEY, generated);
    return generated;
  });
  const [customerOrderIds, setCustomerOrderIds] = useState(() => {
    try {
      const raw = window.localStorage.getItem(CUSTOMER_ORDER_IDS_KEY) || "[]";
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map((value) => String(value)) : [];
    } catch {
      return [];
    }
  });

  const isAdmin = Boolean(adminToken);
  const orderTarget = useMemo(() => getOrderTarget(currentTime), [currentTime]);
  const isOrderingOpen = orderTarget.isOpen;
  const todayOrderKey = getOrderDateKey(currentTime);
  const orderTargetDateKey = orderTarget.orderDateKey || todayOrderKey;
  const customerWarningDisplay = useMemo(
    () => getCustomerWarningDisplay(customerWarning, todayOrderKey),
    [customerWarning, todayOrderKey]
  );
  const customerWarningPreview = useMemo(
    () =>
      getCustomerWarningDisplay(
        { ...customerWarningDraft, enabled: true },
        todayOrderKey
      ),
    [customerWarningDraft, todayOrderKey]
  );
  const warningDateParts = getDateParts(customerWarningDraft.date);
  const warningYearOptions = useMemo(() => {
    const currentYear = Number(getDateParts(todayOrderKey).year) || new Date().getFullYear();
    return [currentYear, currentYear + 1, currentYear + 2].map(String);
  }, [todayOrderKey]);
  const warningDayOptions = useMemo(() => {
    const month = Number(warningDateParts.month);
    const year = Number(warningDateParts.year || warningYearOptions[0]);
    const daysInMonth = getDaysInMonth(year, month || 1);
    return Array.from({ length: daysInMonth }, (_value, index) =>
      String(index + 1).padStart(2, "0")
    );
  }, [warningDateParts.month, warningDateParts.year, warningYearOptions]);
  const todayMealCount = useMemo(
    () =>
      orders
        .filter((order) => getOrderTargetDateKey(order) === orderTargetDateKey)
        .reduce((sum, order) => sum + Number(order.quantity || 0), 0),
    [orders, orderTargetDateKey]
  );
  const outsideOrdersTargetCount = Math.max(
    0,
    Math.floor(Number(outsideOrderCounts[orderTargetDateKey]) || 0)
  );
  const reservedDailyOrders = todayMealCount + outsideOrdersTargetCount;
  const remainingDailyOrders = Math.max(DAILY_ORDER_LIMIT - reservedDailyOrders, 0);
  const isDailyOrderLimitReached = reservedDailyOrders >= DAILY_ORDER_LIMIT;
  const editingReservedQuantity =
    editingOrder && getOrderTargetDateKey(editingOrder) === orderTargetDateKey
      ? Number(editingOrder.quantity || 0)
      : 0;
  const editableQuantityLimit = remainingDailyOrders + editingReservedQuantity;
  const orderFormQuantityLimit = isAdmin
    ? undefined
    : Math.max(editingOrder ? editableQuantityLimit : remainingDailyOrders, 1);
  const isCustomerOrderingClosed = !isAdmin && !isOrderingOpen;
  const isCustomerOrderingUnavailable =
    isCustomerOrderingClosed || (!isAdmin && isDailyOrderLimitReached);

  const menus = useMemo(() => buildMenus(weeklyPlan), [weeklyPlan]);
  const customMenus = useMemo(() => buildCustomMenus(customCards), [customCards]);
  const allMenus = useMemo(() => [...customMenus, ...menus], [menus, customMenus]);
  const featuredDishes = useMemo(
    () => featuredDishIds.map((dishId) => getDishById(dishId)).filter(Boolean),
    [featuredDishIds]
  );
  const sortedMenus = useMemo(() => {
    const dayOrder = new Map(WEEK_DAYS.map((day, index) => [day, index]));
    return [...allMenus].sort((a, b) => {
      const dayA = dayOrder.has(a.day) ? dayOrder.get(a.day) : Number.MAX_SAFE_INTEGER;
      const dayB = dayOrder.has(b.day) ? dayOrder.get(b.day) : Number.MAX_SAFE_INTEGER;
      if (dayA !== dayB) {
        return dayA - dayB;
      }
      return a.name.localeCompare(b.name, "pt");
    });
  }, [allMenus]);
  const todayMenuDay = orderTarget.day;
  const todaysMenus = useMemo(() => {
    return sortedMenus.filter((menu) => menu.day === todayMenuDay);
  }, [sortedMenus, todayMenuDay]);
  const weeklyMenusByDay = useMemo(() => {
    const grouped = WEEK_DAYS.map((day) => {
      const dayMenus = allMenus.filter((menu) => menu.day === day);
      return {
        day,
        specialMenus: [],
        regularMenus: dayMenus,
      };
    });

    const extraDays = allMenus
      .filter((menu) => !WEEK_DAYS.includes(menu.day))
      .reduce((acc, menu) => {
        const existing = acc.find((entry) => entry.day === menu.day);
        if (existing) {
          existing.specialMenus.push(menu);
          return acc;
        }
        acc.push({
          day: menu.day,
          specialMenus: [menu],
          regularMenus: [],
        });
        return acc;
      }, []);

    return [...grouped, ...extraDays];
  }, [allMenus]);
/*  const weeklySpecialDays = useMemo(
    () => weeklyMenusByDay.filter(({ specialMenus }) => specialMenus.length > 0),
    [weeklyMenusByDay]
  );*/
  const weeklyLunchDays = useMemo(
    () => weeklyMenusByDay.filter(({ regularMenus }) => regularMenus.length > 0),
    [weeklyMenusByDay]
  );
  const dishPickerSlot = useMemo(
    () =>
      dishPicker?.type === "weekly"
        ? WEEKLY_MEAL_SLOTS.find((slot) => slot.id === dishPicker.slotId) || null
        : null,
    [dishPicker]
  );
  const dishPickerSelectedId = dishPicker?.type === "weekly"
    ? normalizeDayPlan(weeklyPlan[dishPicker.day])[dishPicker.slotId] || ""
    : dishPicker?.type === "featured"
      ? featuredDishIds[dishPicker.index] || ""
    : "";
  const filteredDishPool = useMemo(() => {
    const searchTerms = dishPickerSearch
      .toLowerCase()
      .split(/\s+/)
      .map((term) => term.trim())
      .filter(Boolean);

    if (searchTerms.length === 0) {
      return DISH_POOL;
    }

    return DISH_POOL.filter((dish) => {
      const searchableText = getDishSearchText(dish);
      return searchTerms.every((term) => searchableText.includes(term));
    });
  }, [dishPickerSearch]);

  const dashboardData = useMemo(() => {
    const activeOrders = orders.filter((order) => !(order.isDelivered && order.isPaid));
    const paymentPendingOrders = orders.filter((order) => order.isDelivered && !order.isPaid);
    const completedOrders = orders.filter((order) => order.isDelivered && order.isPaid);
    const retractableOrders = orders.filter(
      (order) => !order.isDelivered && !order.isPaid && !order.isReceivedByCustomer
    );
    const customerOpenOrders = isAdmin
      ? retractableOrders
      : retractableOrders.filter(
          (order) =>
            (order.customerDeviceId && order.customerDeviceId === customerDeviceId) ||
            customerOrderIds.includes(order.id)
        );
    const lockedDays = new Set(
      activeOrders
        .filter((order) => (order.sourceType || "weekly") === "weekly")
        .map((order) => order.day)
    );

    const summary = allMenus.map((menu) => {
      const relatedOrders = activeOrders.filter((order) => order.menuId === menu.id);
      const totalQuantity = relatedOrders.reduce(
        (sum, order) => sum + Number(order.quantity),
        0
      );

      return {
        ...menu,
        orderCount: relatedOrders.length,
        totalQuantity,
      };
    });

    const activeMeals = summary.reduce((sum, item) => sum + item.totalQuantity, 0);
    const completedMeals = completedOrders.reduce(
      (sum, order) => sum + Number(order.quantity),
      0
    );
    const completedBalance = completedOrders.reduce(
      (sum, order) =>
        sum + Number(order.quantity || 0) * Number(order.price || 0),
      0
    );

    return {
      activeOrders,
      paymentPendingOrders,
      completedOrders,
      retractableOrders: customerOpenOrders,
      lockedDays,
      summary,
      activeMeals,
      completedMeals,
      completedBalance,
    };
  }, [allMenus, customerDeviceId, customerOrderIds, isAdmin, orders]);

  useEffect(() => {
    const timerId = window.setInterval(() => {
      setCurrentTime(new Date());
    }, 30000);

    return () => window.clearInterval(timerId);
  }, []);

  useEffect(() => {
    window.localStorage.setItem(CUSTOMER_ORDER_IDS_KEY, JSON.stringify(customerOrderIds));
  }, [customerOrderIds]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const orderIds = new Set(orders.map((order) => order.id));
    setCustomerOrderIds((current) => current.filter((orderId) => orderIds.has(orderId)));
  }, [isLoading, orders]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const state = await requestJson("/state");
        const mergedPlan = { ...DEFAULT_WEEKLY_PLAN, ...(state.weeklyPlan || {}) };
        setWeeklyPlan(normalizeWeeklyPlan(mergedPlan));
        setCustomCards(Array.isArray(state.customCards) ? state.customCards : []);
        setFeaturedDishIds(
          Array.isArray(state.featuredDishIds)
            ? state.featuredDishIds
                .map((dishId) => String(dishId || "").trim())
                .filter((dishId, index, dishIds) =>
                  getDishById(dishId) && dishIds.indexOf(dishId) === index
                )
                .slice(0, 6)
            : DEFAULT_FEATURED_DISH_IDS
        );
        setCustomerWarning(normalizeCustomerWarning(state.customerWarning));
        setCustomerWarningDraft(normalizeCustomerWarning(state.customerWarning));
        setOutsideOrderCounts(normalizeOutsideOrderCounts(state.outsideOrderCounts));
        setOrders(Array.isArray(state.orders) ? state.orders : []);
      } catch {
        setActionError("Não foi possível carregar os dados do servidor.");
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    if (
      selectedMenu &&
      !editingOrder &&
      !allMenus.some((menu) => menu.id === selectedMenu.id)
    ) {
      setSelectedMenu(null);
    }
  }, [allMenus, editingOrder, selectedMenu]);

  useEffect(() => {
    const validateSession = async () => {
      if (!adminToken) {
        return;
      }

      try {
        await requestJson("/admin/session", { token: adminToken });
      } catch {
        window.localStorage.removeItem(ADMIN_TOKEN_KEY);
        setAdminToken("");
        setView("menu");
      }
    };

    validateSession();
  }, [adminToken]);

  useEffect(() => {
    if (!dishPicker) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeDishPicker();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [dishPicker]);

  const resetView = (nextView) => {
    setActionError("");
    setOrderConfirmation("");
    setSelectedMenu(null);
    setEditingOrder(null);
    setPendingAction(null);
    closeDishPicker();

    if (["admin", "planner", "delivery"].includes(nextView) && !isAdmin) {
      setPendingAdminView(nextView);
      setView("login");
      return;
    }

    setView(nextView);
  };

  const handleOrderClick = (menu) => {
    if (isCustomerOrderingClosed) {
      setActionError(orderTarget.closedDescription);
      return;
    }

    if (!isAdmin && isDailyOrderLimitReached) {
      setActionError(`O limite de pedidos para ${orderTarget.label} foi atingido.`);
      return;
    }

    setSelectedMenu(menu);
    setEditingOrder(null);
    setPendingAction(null);
    setOrderConfirmation("");
    setView("menu");
    setActionError("");
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: name === "quantity" ? Number(value) : value,
    }));
  };

  const handleQuantityChange = (event) => {
    const input = event.currentTarget;
    const rawValue = input.value;

    if (rawValue === "") {
      input.setCustomValidity("");
      setFormData((current) => ({
        ...current,
        quantity: "",
      }));
      return;
    }

    const quantity = Number(rawValue);

    if (quantity < 1) {
      input.setCustomValidity("A quantidade mínima é 1.");
    } else if (
      Number.isFinite(orderFormQuantityLimit) &&
      quantity > orderFormQuantityLimit
    ) {
      input.setCustomValidity(
        `Só pode pedir até ${orderFormQuantityLimit} refeições.`
      );
    } else {
      input.setCustomValidity("");
    }

    setFormData((current) => ({
      ...current,
      quantity,
    }));
  };

  const updateOutsideOrderCount = async (nextValue) => {
    const nextCount = Math.min(
      DAILY_ORDER_LIMIT,
      Math.max(0, Math.floor(Number(nextValue) || 0))
    );
    const previousCounts = outsideOrderCounts;

    setOutsideOrderCounts((current) => ({
      ...current,
      [orderTargetDateKey]: nextCount,
    }));
    setActionError("");

    try {
      const response = await requestJson("/outside-orders", {
        method: "PUT",
        token: adminToken,
        body: {
          dateKey: orderTargetDateKey,
          count: nextCount,
        },
      });

      setOutsideOrderCounts(normalizeOutsideOrderCounts(response.outsideOrderCounts));
    } catch (error) {
      setOutsideOrderCounts(previousCounts);
      setActionError(error.message || "Não foi possível guardar os pedidos fora do site.");
    }
  };

  const editOrder = (order) => {
    const menu = allMenus.find((item) => item.id === order.menuId) || {
      id: order.menuId,
      day: order.day,
      name: order.menuName,
      price: order.price,
      description: "",
      sourceType: order.sourceType || "weekly",
    };

    setEditingOrder(order);
    setSelectedMenu(menu);
    setFormData({
      customerName: String(order.customerName || ""),
      phone: String(order.phone || ""),
      address: String(order.address || ""),
      quantity: Number(order.quantity || 1),
      extraNotes: String(order.extraNotes || ""),
    });
    setPendingAction(null);
    setOrderConfirmation("");
    setActionError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedMenu) {
      return;
    }

    if (!editingOrder && isCustomerOrderingClosed) {
      setActionError(orderTarget.closedDescription);
      return;
    }

    if (!editingOrder && !isAdmin && isDailyOrderLimitReached) {
      setActionError(`O limite de pedidos para ${orderTarget.label} foi atingido.`);
      return;
    }

    if (!isAdmin && Number(formData.quantity) > orderFormQuantityLimit) {
      setActionError(`Só pode pedir até ${orderFormQuantityLimit} refeições.`);
      return;
    }

    if (Number(formData.quantity) < 1) {
      setActionError("A quantidade mínima é 1.");
      return;
    }

    setActionError("");
    setOrderConfirmation("");

    const payload = {
      customerName: formData.customerName.trim(),
      phone: formData.phone.trim(),
      address: formData.address.trim(),
      quantity: Number(formData.quantity),
      extraNotes: formData.extraNotes.trim(),
      menuId: selectedMenu.id,
      menuName: selectedMenu.name,
      day: selectedMenu.day,
      price: selectedMenu.price,
      sourceType: selectedMenu.sourceType || "weekly",
      customerDeviceId,
      orderDateKey: orderTargetDateKey,
    };

    try {
      if (editingOrder) {
        const response = await requestJson(`/orders/${editingOrder.id}/edit`, {
          method: "PATCH",
          token: isAdmin ? adminToken : "",
          body: payload,
        });

        setOrders((currentOrders) =>
          currentOrders.map((order) =>
            order.id === editingOrder.id ? response.order : order
          )
        );
        setEditingOrder(null);
        setSelectedMenu(null);
        setFormData(emptyForm);
        return;
      }

      const response = await requestJson("/orders", {
        method: "POST",
        token: isAdmin ? adminToken : "",
        body: payload,
      });

      setOrders((currentOrders) => [
        ...currentOrders,
        {
          ...response.order,
          extraNotes:
            typeof response.order?.extraNotes === "string"
              ? response.order.extraNotes
              : payload.extraNotes,
        },
      ]);
      setCustomerOrderIds((current) =>
        current.includes(response.order.id) ? current : [...current, response.order.id]
      );
      setSelectedMenu(null);
      setEditingOrder(null);
      setFormData(emptyForm);
      if (!isAdmin) {
        setOrderConfirmation("Pedido confirmado, agora é só aguardar pela refeição :)");
      }
    } catch (error) {
      setActionError(error.message || "Não foi possível guardar o pedido.");
    }
  };

  const handleWeeklyPlanChange = async (day, slotId, dishId) => {
    const nextPlan = {
      ...weeklyPlan,
      [day]: {
        ...normalizeDayPlan(weeklyPlan[day]),
        [slotId]: dishId,
      },
    };

    setActionError("");

    try {
      const response = await requestJson("/weekly-plan", {
        method: "PUT",
        token: adminToken,
        body: { weeklyPlan: nextPlan },
      });

      setWeeklyPlan(normalizeWeeklyPlan(response.weeklyPlan || nextPlan));
    } catch (error) {
      setActionError(error.message || "Não foi possível atualizar o plano semanal.");
    }
  };

  const openDishPicker = (day, slotId) => {
    setDishPicker({ type: "weekly", day, slotId });
    setDishPickerSearch("");
    setActionError("");
  };

  const openFeaturedDishPicker = (index) => {
    setDishPicker({ type: "featured", index });
    setDishPickerSearch("");
    setActionError("");
  };

  const closeDishPicker = () => {
    setDishPicker(null);
    setDishPickerSearch("");
  };

  const persistFeaturedDishIds = async (nextDishIds) => {
    setActionError("");

    try {
      const response = await requestJson("/featured-dishes", {
        method: "PUT",
        token: adminToken,
        body: { featuredDishIds: nextDishIds },
      });

      setFeaturedDishIds(
        Array.isArray(response.featuredDishIds)
          ? response.featuredDishIds
          : nextDishIds
      );
    } catch (error) {
      setActionError(error.message || "Não foi possível atualizar os pratos em destaque.");
    }
  };

  const updateFeaturedDish = async (index, dishId) => {
    const currentIds = [...featuredDishIds];

    if (!dishId) {
      await persistFeaturedDishIds(currentIds.filter((_dishId, currentIndex) => currentIndex !== index));
      return;
    }

    const duplicateIndex = currentIds.findIndex(
      (currentDishId, currentIndex) => currentDishId === dishId && currentIndex !== index
    );

    if (duplicateIndex >= 0) {
      setActionError("Este prato já está em destaque.");
      closeDishPicker();
      return;
    }

    if (index >= currentIds.length) {
      currentIds.push(dishId);
    } else {
      currentIds[index] = dishId;
    }

    await persistFeaturedDishIds(currentIds.slice(0, 6));
  };

  const moveFeaturedDish = async (index, direction) => {
    const nextIndex = index + direction;

    if (nextIndex < 0 || nextIndex >= featuredDishIds.length) {
      return;
    }

    const nextDishIds = [...featuredDishIds];
    const currentDishId = nextDishIds[index];
    nextDishIds[index] = nextDishIds[nextIndex];
    nextDishIds[nextIndex] = currentDishId;

    await persistFeaturedDishIds(nextDishIds);
  };

  const selectDishFromPicker = async (dishId) => {
    if (!dishPicker) {
      return;
    }

    if (dishPicker.type === "featured") {
      await updateFeaturedDish(dishPicker.index, dishId);
      closeDishPicker();
      return;
    }

    await handleWeeklyPlanChange(dishPicker.day, dishPicker.slotId, dishId);
    closeDishPicker();
  };

  const persistCustomCards = async (nextCards) => {
    setActionError("");

    try {
      const response = await requestJson("/custom-cards", {
        method: "PUT",
        token: adminToken,
        body: { customCards: nextCards },
      });

      setCustomCards(response.customCards);
    } catch (error) {
      setActionError(error.message || "Não foi possível atualizar os cartões personalizados.");
    }
  };

  const addCustomCard = () => {
    setCustomCardDraft(emptyCustomCardDraft);
    setIsAddingCustomCard(true);
    setEditingCustomCardId("");
    setPendingCustomCardDeleteId(null);
  };

  const cancelAddCustomCard = () => {
    setCustomCardDraft(emptyCustomCardDraft);
    setIsAddingCustomCard(false);
    setEditingCustomCardId("");
  };

  const editCustomCard = (card) => {
    setCustomCardDraft({
      day: String(card.day || ""),
      dishName: String(card.dishName || ""),
      description: String(card.description || ""),
      imageUrl: String(card.imageUrl || ""),
      price: String(card.price || ""),
      lactoseFree: Boolean(card.lactoseFree ?? card.dairyFree),
      glutenFree: Boolean(card.glutenFree),
      vegan: Boolean(card.vegan),
      enabled: Boolean(card.enabled),
    });
    setEditingCustomCardId(String(card.id || ""));
    setPendingCustomCardDeleteId(null);
    setIsAddingCustomCard(true);
    setActionError("");
  };

  const handleCustomCardDraftChange = (field, value) => {
    setCustomCardDraft((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const confirmAddCustomCard = async () => {
    const day = customCardDraft.day.trim();
    const dishName = customCardDraft.dishName.trim();
    const description = customCardDraft.description.trim();
    const imageUrl = normalizeImageUrl(customCardDraft.imageUrl);
    const price = Number(customCardDraft.price);

    if (!day || !dishName || !description || !Number.isFinite(price) || price <= 0) {
      setActionError(
        "Preencha o dia, o nome do prato, a descrição e um preço válido antes de confirmar."
      );
      return;
    }

    const nextCardPayload = {
      day,
      title: `Prato para ${day}`,
      dishName,
      description,
      imageUrl,
      price,
      lactoseFree: Boolean(customCardDraft.lactoseFree),
      dairyFree: Boolean(customCardDraft.lactoseFree),
      glutenFree: Boolean(customCardDraft.glutenFree),
      vegan: Boolean(customCardDraft.vegan),
      enabled: Boolean(customCardDraft.enabled),
    };

    const nextCards = editingCustomCardId
      ? customCards.map((card) =>
          card.id === editingCustomCardId
            ? { ...card, ...nextCardPayload }
            : card
        )
      : [...customCards, { id: `card-${Date.now()}`, ...nextCardPayload }];

    await persistCustomCards(nextCards);
    setCustomCardDraft(emptyCustomCardDraft);
    setIsAddingCustomCard(false);
    setEditingCustomCardId("");
  };

  const deleteCustomCard = async (cardId) => {
    const nextCards = customCards.filter((card) => card.id !== cardId);
    await persistCustomCards(nextCards);
    setPendingCustomCardDeleteId(null);
  };

  const updateOrderStep = async (orderId, step) => {
    setActionError("");

    try {
      const response = await requestJson(`/orders/${orderId}/step`, {
        method: "PATCH",
        token: adminToken,
        body: { step },
      });

      setOrders((currentOrders) =>
        currentOrders.map((order) =>
          order.id === orderId
            ? {
                ...response.order,
                extraNotes:
                  typeof response.order?.extraNotes === "string"
                    ? response.order.extraNotes
                    : order.extraNotes || "",
              }
            : order
        )
      );
    } catch (error) {
      setActionError(error.message || "Não foi possível atualizar o estado do pedido.");
    }
  };

  const deleteOrder = async (orderId, scope) => {
    setActionError("");

    const isCustomerDelete = scope === "customer";

    try {
      await requestJson(
        isCustomerDelete ? `/orders/${orderId}/retract` : `/orders/${orderId}`,
        {
          method: "DELETE",
          token: isCustomerDelete ? "" : adminToken,
        }
      );

      setOrders((currentOrders) =>
        currentOrders.filter((order) => order.id !== orderId)
      );
      setCustomerOrderIds((current) => current.filter((id) => id !== orderId));
      setPendingAction(null);
    } catch (error) {
      setActionError(error.message || "Não foi possível eliminar o pedido.");
    }
  };

  const beginDangerAction = (orderId, type, scope) => {
    setPendingAction({ orderId, type, scope });
  };

  const cancelPendingAction = () => {
    setPendingAction(null);
  };

  const confirmPendingAction = async () => {
    if (!pendingAction) {
      return;
    }

    if (pendingAction.scope === "customer-edit") {
      const order = orders.find((item) => item.id === pendingAction.orderId);
      if (order) {
        editOrder(order);
      }
      setPendingAction(null);
      return;
    }

    if (pendingAction.scope === "admin-completed-reopen") {
      await updateOrderStep(pendingAction.orderId, "reopen");
      setPendingAction(null);
      return;
    }

    await deleteOrder(pendingAction.orderId, pendingAction.scope);
  };

  const cancelOrder = () => {
    setSelectedMenu(null);
    setEditingOrder(null);
    setFormData(emptyForm);
  };

  const handleLoginChange = (event) => {
    const { name, value } = event.target;
    setLoginForm((current) => ({ ...current, [name]: value }));
  };

  const handleAdminLogin = async (event) => {
    event.preventDefault();
    setLoginError("");

    try {
      const response = await requestJson("/admin/login", {
        method: "POST",
        body: {
          username: loginForm.username.trim(),
          password: loginForm.password,
        },
      });

      window.localStorage.setItem(ADMIN_TOKEN_KEY, response.token);
      setAdminToken(response.token);
      setLoginForm(emptyLoginForm);
      setView(pendingAdminView);
    } catch (error) {
      setLoginError(error.message || "Não foi possível iniciar sessão.");
    }
  };

  const handleAdminLogout = async () => {
    if (!adminToken) {
      return;
    }

    try {
      await requestJson("/admin/logout", {
        method: "POST",
        token: adminToken,
      });
    } catch {
      // Ignore logout API failures and continue local cleanup.
    }

    window.localStorage.removeItem(ADMIN_TOKEN_KEY);
    setAdminToken("");
    setView("menu");
    setActionError("");
  };

  const downloadCurrentState = () => {
    const activeRows = dashboardData.activeOrders.map((order) => [
      "Em processamento/aguardando pagamento",
      order.id,
      order.customerName,
      order.day,
      order.menuName,
      order.quantity,
      order.price,
      order.isDelivered ? "Aguardando pagamento" : "Aguardando entrega",
      order.phone,
      order.address,
      order.extraNotes || "",
      formatExportDateTime(order.createdAt),
      formatExportDateTime(order.paidAt),
    ]);

    const completedRows = dashboardData.completedOrders.map((order) => [
      "Concluído",
      order.id,
      order.customerName,
      order.day,
      order.menuName,
      order.quantity,
      order.price,
      "Concluído",
      order.phone,
      order.address,
      order.extraNotes || "",
      formatExportDateTime(order.createdAt),
      formatExportDateTime(order.paidAt),
    ]);

    const headers = [
      "Tipo",
      "ID Pedido (UUID do sistema)",
      "Cliente",
      "Dia",
      "Menu",
      "Quantidade",
      "Preço EUR",
      "Estado",
      "Telefone",
      "Morada",
      "Pedidos extra/observações/alergias",
      "Criado em",
      "Pago em",
    ];

    const summaryRows = [
      ["Resumo", "", "", "", "", "", "", "", "", "", "", "", ""],
      ["Refeições concluídas", dashboardData.completedMeals, "", "", "", "", "", "", "", "", "", "", ""],
      ["EUR concluídos", dashboardData.completedBalance, "", "", "", "", "", "", "", "", "", "", ""],
    ];

    const allRows = [headers, ...activeRows, ...completedRows, ...summaryRows];
    const csv = allRows
      .map((row) => row.map((cell) => formatCsvCell(cell)).join(";"))
      .join("\n");

    const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    const dateStamp = new Date().toISOString().slice(0, 11);
    link.href = url;
    link.download = `estado-pedidos-${dateStamp}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const resetConcludedOrders = async () => {
    setActionError("");

    try {
      const response = await requestJson("/admin/cleaning/reset-concluded", {
        method: "POST",
        token: adminToken,
      });
      setOrders(Array.isArray(response.orders) ? response.orders : []);
      setPendingCleaningAction("");
    } catch (error) {
      setActionError(error.message || "Não foi possível limpar os pedidos concluídos.");
    }
  };

  const resetAllOrders = async () => {
    setActionError("");

    try {
      const response = await requestJson("/admin/cleaning/reset-all-orders", {
        method: "POST",
        token: adminToken,
      });
      setOrders(Array.isArray(response.orders) ? response.orders : []);
      setPendingCleaningAction("");
    } catch (error) {
      setActionError(error.message || "Não foi possível efetuar o reset geral.");
    }
  };

  const handleCustomerWarningDraftChange = (field, value) => {
    setCustomerWarningDraft((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleCustomerWarningDatePartChange = (field, value) => {
    const todayParts = getDateParts(todayOrderKey);

    setCustomerWarningDraft((current) => {
      const currentParts = getDateParts(current.date);
      const nextParts = {
        day: currentParts.day || todayParts.day,
        month: currentParts.month || todayParts.month,
        year: currentParts.year || todayParts.year,
        [field]: value,
      };

      return {
        ...current,
        date: buildDateKeyFromParts(nextParts),
      };
    });
  };

  const saveCustomerWarning = async () => {
    const nextWarning = {
      enabled: Boolean(customerWarningDraft.enabled),
      message: String(customerWarningDraft.message || "").trim(),
      date: String(customerWarningDraft.date || "").trim(),
    };

    if (nextWarning.enabled && !nextWarning.message) {
      setActionError("Escreva uma mensagem antes de publicar o aviso.");
      return;
    }

    setActionError("");

    try {
      const response = await requestJson("/customer-warning", {
        method: "PUT",
        token: adminToken,
        body: { customerWarning: nextWarning },
      });
      const savedWarning = normalizeCustomerWarning(response.customerWarning);
      setCustomerWarning(savedWarning);
      setCustomerWarningDraft(savedWarning);
    } catch (error) {
      setActionError(error.message || "Não foi possível guardar o aviso.");
    }
  };

  const disableCustomerWarning = async () => {
    const nextWarning = {
      ...customerWarningDraft,
      enabled: false,
    };

    setActionError("");

    try {
      const response = await requestJson("/customer-warning", {
        method: "PUT",
        token: adminToken,
        body: { customerWarning: nextWarning },
      });
      const savedWarning = normalizeCustomerWarning(response.customerWarning);
      setCustomerWarning(savedWarning);
      setCustomerWarningDraft(savedWarning);
    } catch (error) {
      setActionError(error.message || "Não foi possível desativar o aviso.");
    }
  };

  const renderCleaningActionButton = ({
    actionId,
    label,
    confirmLabel,
    buttonClassName,
    confirmButtonClassName,
    onConfirm,
  }) =>
    pendingCleaningAction === actionId ? (
      <div className="confirm-actions">
        <button type="button" className={confirmButtonClassName} onClick={onConfirm}>
          {confirmLabel}
        </button>
        <button
          type="button"
          className="secondary-button secondary-button-small"
          onClick={() => setPendingCleaningAction("")}
        >
          Voltar
        </button>
      </div>
    ) : (
      <button
        type="button"
        className={buttonClassName}
        onClick={() => setPendingCleaningAction(actionId)}
      >
        {label}
      </button>
    );

  const renderDangerAction = ({
    order,
    scope,
    buttonLabel,
    confirmLabel,
    buttonClassName = "danger-button",
    confirmButtonClassName = "danger-button",
    confirmButtonLabel = "Confirmar",
    confirmBoxClassName = "",
    inlineConfirm = false,
  }) => {
    const isPending =
      pendingAction?.orderId === order.id &&
      pendingAction?.scope === scope &&
      pendingAction?.type === buttonLabel;

    if (isPending) {
      if (inlineConfirm) {
        return (
          <div className="confirm-actions">
            <button
              type="button"
              className={confirmButtonClassName}
              onClick={confirmPendingAction}
            >
              {confirmButtonLabel}
            </button>
            <button
              type="button"
              className="secondary-button secondary-button-small"
              onClick={cancelPendingAction}
            >
              Cancelar
            </button>
          </div>
        );
      }

      return (
        <div className={`confirm-box ${confirmBoxClassName}`.trim()}>
          <p>{confirmLabel}</p>
          <div className="confirm-actions">
            <button
              type="button"
              className={confirmButtonClassName}
              onClick={confirmPendingAction}
            >
              {confirmButtonLabel}
            </button>
            <button
              type="button"
              className="secondary-button secondary-button-small"
              onClick={cancelPendingAction}
            >
              Cancelar
            </button>
          </div>
        </div>
      );
    }

    return (
      <button
        type="button"
        className={buttonClassName}
        onClick={() => beginDangerAction(order.id, buttonLabel, scope)}
      >
        {buttonLabel}
      </button>
    );
  };

  const renderCustomerOrdersPanel = () =>
    dashboardData.retractableOrders.length > 0 ? (
      <section className="panel customer-panel customer-orders-panel">
        <div className="section-heading compact">
          <div>
            <p className="section-kicker">Controlo do cliente</p>
            <h2>Os seus pedidos</h2>
          </div>
          <span className="muted-pill">
            {dashboardData.retractableOrders.length} Anuláveis
          </span>
        </div>

        <div className="customer-orders-list">
          {dashboardData.retractableOrders.map((order) => {
            const customerPendingScope =
              pendingAction?.orderId === order.id &&
              ["customer", "customer-edit"].includes(pendingAction.scope)
                ? pendingAction.scope
                : null;

            return (
              <article className="customer-order-card" key={order.id}>
                <div>
                  <p className="menu-day">{order.day}</p>
                  <h3>{order.customerName}</h3>
                  <p>
                    {order.menuName} • x{order.quantity}
                  </p>
                  <p>{order.address}</p>
                  <p>{order.phone}</p>
                </div>
                <div className="order-note-box">
                  <p>{order.extraNotes || "Sem pedidos extra, observações ou alergias."}</p>
                </div>
                <div className="customer-order-actions">
                  {customerPendingScope !== "customer"
                    ? renderDangerAction({
                        order,
                        scope: "customer-edit",
                        buttonLabel: "Editar pedido",
                        confirmLabel: "Editar os dados deste pedido?",
                        buttonClassName: "secondary-button",
                        confirmButtonClassName: "success-outline-button",
                        confirmButtonLabel: "Confirmar edição",
                        inlineConfirm: true,
                      })
                    : null}
                  {customerPendingScope !== "customer-edit"
                    ? renderDangerAction({
                        order,
                        scope: "customer",
                        buttonLabel: "Anular pedido",
                        confirmLabel: "Anular este pedido? Esta ação não pode ser desfeita.",
                        inlineConfirm: true,
                      })
                    : null}
                </div>
              </article>
            );
          })}
        </div>
      </section>
    ) : null;

  const renderCustomerMenuPanel = () => (
    <section className="panel customer-panel customer-menu-panel">
      <div className="section-heading">
        <div>
          <p className="section-kicker">Vista do cliente</p>
          <h2>{orderTarget.label === "hoje" ? "Menus do dia" : "Menus de amanhã"}</h2>
        </div>
        <span className="muted-pill">
          {remainingDailyOrders}/{DAILY_ORDER_LIMIT} {orderTarget.availableText}
        </span>
      </div>

      {customerWarningDisplay.isVisible ? (
        <div className="customer-warning-notice" role="status">
          <p>{customerWarningDisplay.message}</p>
          {customerWarningDisplay.dateLabel ? (
            <span>{customerWarningDisplay.dateLabel}</span>
          ) : null}
        </div>
      ) : null}

      {isCustomerOrderingClosed || (!isAdmin && isDailyOrderLimitReached) ? (
        <div className="order-cutoff-notice" role="status">
          <p>
            {isDailyOrderLimitReached
              ? `Limite de pedidos para ${orderTarget.label} atingido.`
              : orderTarget.closedTitle}
          </p>
          <span>
            {isDailyOrderLimitReached
              ? `Já não existem pedidos disponíveis para ${orderTarget.label}.`
              : orderTarget.closedDescription}
          </span>
        </div>
      ) : null}

      {todaysMenus.length === 0 ? (
        <div className="empty-state">
          <p>
            {todayMenuDay
              ? `Ainda não existem pratos selecionados para ${todayMenuDay}.`
              : `Ainda não existem pratos selecionados para ${orderTarget.label}.`}
          </p>
          <span>
            {todayMenuDay
              ? `O administrador tem de publicar os pratos de ${orderTarget.label} no planeador.`
              : "Pode sempre consultar e encomendar os pratos especiais quando disponíveis."}
          </span>
        </div>
      ) : (
        <>
          <div className="menu-grid">
            {todaysMenus.map((menu) => (
              <div className="menu-entry" key={menu.id}>
                <article className="menu-card">
                  <div className="menu-card-top">
                    <p className="menu-day">{menu.day}</p>
                    <span className="price-badge">{menu.price} EUR</span>
                  </div>
                  <h3>{formatMenuName(menu)}</h3>
                  <p>{menu.description}</p>
                  <button
                    type="button"
                    className="primary-button"
                    disabled={isCustomerOrderingUnavailable}
                    onClick={() => handleOrderClick(menu)}
                  >
                    {isCustomerOrderingUnavailable ? "Pedidos indisponíveis" : "Selecionar menu"}
                  </button>
                </article>
                <article className="menu-image-card" aria-label={`${menu.name} image`}>
                  <img
                    src={menu.imageUrl || MENU_PLACEHOLDER_IMAGE}
                    alt={`${menu.name} preview`}
                    className="menu-image"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    onError={(event) => {
                      if (event.currentTarget.src.includes(MENU_PLACEHOLDER_IMAGE)) {
                        return;
                      }

                      event.currentTarget.src = MENU_PLACEHOLDER_IMAGE;
                    }}
                  />
                </article>
              </div>
            ))}
          </div>

          <article className="weekly-ementa-card">
            <h3>Ementa Semanal</h3>
            {/*<p className="weekly-group-title">Pratos especiais</p>
            <div className="weekly-ementa-content">
              {weeklySpecialDays.length > 0 ? (
                weeklySpecialDays.map(({ day, specialMenus }) => (
                  <section className="weekly-day-block" key={`special-${day}`}>
                    <p className="weekly-day-title">{day}</p>
                    <ul className="weekly-dish-list">
                      {specialMenus.map((menu) => (
                        <li key={menu.id}>
                          <span>{formatMenuName(menu)}</span>
                          <span>{menu.price} EUR</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))
              ) : (
                <p className="weekly-empty-text">Sem pratos especiais.</p>
              )}
            </div>
            */}
            <p className="weekly-group-title">Almoços</p>
            <div className="weekly-ementa-content">
              {weeklyLunchDays.map(({ day, regularMenus }) => (
                <section className="weekly-day-block" key={`lunch-${day}`}>
                  <p className="weekly-day-title">{day}</p>
                  <ul className="weekly-dish-list">
                    {regularMenus.map((menu) => (
                      <li key={menu.id}>
                        <span>{formatMenuName(menu)}</span>
                        <span>{menu.price} EUR</span>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          </article>
        </>
      )}
    </section>
  );

  const renderCustomerView = () => (
    <div className="customer-stack">
      {renderCustomerOrdersPanel()}
      {renderCustomerMenuPanel()}
    </div>
  );

  const renderFeaturedDishesView = () => (
    <section className="featured-dishes-page">
      <div className="section-heading">
        <div>
          <p className="section-kicker">Pratos</p>
          <h2>Os nossos pratos</h2>
        </div>
        <span className="muted-pill">{featuredDishes.length}/{DISH_POOL.length} Em destaque</span>
      </div>

      {featuredDishes.length === 0 ? (
        <div className="empty-state">
          <p>Ainda não existem pratos em destaque.</p>
          <span>Os pratos escolhidos pelo administrador aparecem aqui.</span>
        </div>
      ) : (
        <div className="featured-dish-grid">
          {featuredDishes.map((dish) => (
            <article className="featured-dish-card" key={dish.id}>
              <div className="featured-dish-image-wrap">
                <img
                  src={DISH_IMAGE_BY_ID[dish.id] || MENU_PLACEHOLDER_IMAGE}
                  alt={`${dish.name} preview`}
                  className="featured-dish-image"
                  loading="lazy"
                  onError={(event) => {
                    if (event.currentTarget.src.includes(MENU_PLACEHOLDER_IMAGE)) {
                      return;
                    }

                    event.currentTarget.src = MENU_PLACEHOLDER_IMAGE;
                  }}
                />
              </div>
              <div className="featured-dish-body">
                <h3>{dish.name}</h3>
                <div className="featured-dish-tags">
                  <span>{getDishCategoryLabel(dish)}</span>
                  {getDishDietaryLabels(dish).map((label) => (
                    <span key={label}>{label}</span>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {isAdmin ? (
        <section className="featured-admin-box">
          <div className="section-heading compact">
            <div>
              <p className="section-kicker">Administração</p>
              <h2>Pratos em destaque</h2>
            </div>
            <span className="muted-pill">{featuredDishIds.length}/{DISH_POOL.length} Selecionados</span>
          </div>

          <div className="featured-admin-list">
            {featuredDishIds.map((dishId, index) => {
              const dish = getDishById(dishId);

              if (!dish) {
                return null;
              }

              return (
                <article className="featured-admin-card" key={`${dishId}-${index}`}>
                  <img
                    src={DISH_IMAGE_BY_ID[dish.id] || MENU_PLACEHOLDER_IMAGE}
                    alt=""
                    className="featured-admin-thumb"
                    loading="lazy"
                    onError={(event) => {
                      if (event.currentTarget.src.includes(MENU_PLACEHOLDER_IMAGE)) {
                        return;
                      }

                      event.currentTarget.src = MENU_PLACEHOLDER_IMAGE;
                    }}
                  />
                  <div>
                    <p className="menu-day">Destaque {index + 1}</p>
                    <h3>{dish.name}</h3>
                  </div>
                  <div className="featured-admin-actions">
                    <button
                      type="button"
                      className="secondary-button secondary-button-small featured-move-button"
                      onClick={() => moveFeaturedDish(index, -1)}
                      disabled={index === 0}
                      aria-label={`Mover ${dish.name} para cima`}
                      title="Mover para cima"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="secondary-button secondary-button-small featured-move-button"
                      onClick={() => moveFeaturedDish(index, 1)}
                      disabled={index === featuredDishIds.length - 1}
                      aria-label={`Mover ${dish.name} para baixo`}
                      title="Mover para baixo"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="secondary-button secondary-button-small"
                      onClick={() => openFeaturedDishPicker(index)}
                    >
                      Trocar
                    </button>
                    <button
                      type="button"
                      className="danger-button secondary-button-small"
                      onClick={() => updateFeaturedDish(index, "")}
                    >
                      Remover
                    </button>
                  </div>
                </article>
              );
            })}
          </div>

          {featuredDishIds.length < 6 ? (
            <button
              type="button"
              className="primary-button featured-add-button"
              onClick={() => openFeaturedDishPicker(featuredDishIds.length)}
            >
              Adicionar prato
            </button>
          ) : null}
        </section>
      ) : null}
    </section>
  );

  const renderAboutView = () => (
    <section className="auth-wrapper">
      <div className="section-heading compact">
        <div>
          <p className="section-kicker">Sobre nós</p>
          <h2>Informação da empresa</h2>
        </div>
        <span className="muted-pill">Apoio ao cliente</span>
      </div>

      <p className="auth-copy">
        Somos uma pequena equipa com gosto pela comida portuguesa e brasileira.
        O nosso objetivo é simples: entregar refeições frescas e acessíveis no seu local de trabalho durante a semana.
      </p>

      <p className="auth-copy">
        Refeições a custos acessíveis, confecionadas na hora, com ingredientes frescos e da época. Pedidos feitos na véspera e no próprio dia, entre as 14 e as 11 horas. As entregas são feitas em Lisboa, de segunda a sexta-feira, ao almoço, diretamente no seu local de trabalho e limitadas ao número de refeições disponíveis.
      </p>

      <p className="auth-copy"> {/* helper-text */}
        Se tiver alguma dúvida, por favor, contacte o nosso serviço de apoio.
      </p>

      <div className="selected-menu-card">
        <h3>Contactos</h3>
        <p>
          Telefone: <a href="tel:+351923229476">+351 923 229 476</a>
        </p>
        <p>
          Email: <a href="mailto:alfacinhacapixaba@gmail.com">alfacinhacapixaba@gmail.com</a>
        </p>
      </div>

      <div className="form-actions">
        <button type="button" className="primary-button" onClick={() => resetView("login")}>
          Login
        </button>
      </div>
    </section>
  );

  const renderDishPicker = () =>
    dishPicker ? (
      <div className="dish-picker-backdrop" role="presentation" onMouseDown={closeDishPicker}>
        <section
          className="dish-picker-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="dish-picker-title"
          onMouseDown={(event) => event.stopPropagation()}
        >
          <div className="dish-picker-header">
            <div>
              <p className="section-kicker">
                {dishPicker.type === "featured"
                  ? "Pratos em destaque"
                  : `${dishPicker.day} · ${dishPickerSlot?.label || "Prato"}`}
              </p>
              <h2 id="dish-picker-title">
                {dishPicker.type === "featured" ? "Escolher destaque" : "Escolher prato"}
              </h2>
            </div>
            <button
              type="button"
              className="secondary-button secondary-button-small"
              onClick={closeDishPicker}
            >
              Fechar
            </button>
          </div>

          <label className="dish-picker-search">
            Pesquisar prato
            <input
              autoFocus
              value={dishPickerSearch}
              onChange={(event) => setDishPickerSearch(event.target.value)}
              placeholder="Ex: bacalhau, frango, salada, sem lactose..."
            />
          </label>

          <div className="dish-picker-results" role="listbox" aria-label="Pratos disponíveis">
            <button
              type="button"
              className={
                dishPickerSelectedId === ""
                  ? "dish-picker-option selected"
                  : "dish-picker-option"
              }
              onClick={() => selectDishFromPicker("")}
              role="option"
              aria-selected={dishPickerSelectedId === ""}
            >
              <span className="dish-picker-empty-icon" aria-hidden="true">-</span>
              <span>
                <strong>Nenhum</strong>
                <small>
                  {dishPicker.type === "featured"
                    ? " Remover este destaque"
                    : " Deixar este campo vazio na ementa semanal"}
                </small>
              </span>
            </button>

            {filteredDishPool.map((dish) => {
              const dietaryLabels = getDishDietaryLabels(dish);
              const isSelected = dishPickerSelectedId === dish.id;

              return (
                <button
                  type="button"
                  className={isSelected ? "dish-picker-option selected" : "dish-picker-option"}
                  key={dish.id}
                  onClick={() => selectDishFromPicker(dish.id)}
                  role="option"
                  aria-selected={isSelected}
                >
                  <img
                    src={DISH_IMAGE_BY_ID[dish.id] || MENU_PLACEHOLDER_IMAGE}
                    alt=""
                    loading="lazy"
                    className="dish-picker-thumb"
                    onError={(event) => {
                      if (event.currentTarget.src.includes(MENU_PLACEHOLDER_IMAGE)) {
                        return;
                      }

                      event.currentTarget.src = MENU_PLACEHOLDER_IMAGE;
                    }}
                  />
                  <span className="dish-picker-option-main">
                    <strong>{dish.name}</strong>
                    <small>{dish.description}</small>
                  </span>
                  <span className="dish-picker-option-meta">
                    <span>{getDishCategoryLabel(dish)}</span>
                    <span>{dish.price} EUR</span>
                    {dietaryLabels.map((label) => (
                      <span key={label}>{label}</span>
                    ))}
                  </span>
                </button>
              );
            })}

            {filteredDishPool.length === 0 ? (
              <div className="empty-state">
                <p>Nenhum prato encontrado.</p>
                <span>Tente pesquisar por outro nome, ingrediente ou etiqueta.</span>
              </div>
            ) : null}
          </div>
        </section>
      </div>
    ) : null;

  const renderMenuPlanner = () => (
    <>
      <div className="section-heading">
        <div>
          <p className="section-kicker">Configuração de administrador</p>
          <h2>Planeador de menu semanal</h2>
        </div>
        <span className="muted-pill">{DISH_POOL.length} Pratos disponíveis</span>
      </div>

      <div className="planner-stack">
        <div className="planner-box standalone">
          <p className="planner-intro">
            Selecione até 3 pratos para cada dia útil (carne, peixe e vegano).
            As opções "Nenhum" não aparecem na ementa do cliente.
            Os dias com pedidos em aberto ficam bloqueados para evitar trocas acidentais.
          </p>

          <div className="planner-grid">
            {WEEK_DAYS.map((day) => {
              const isLocked = dashboardData.lockedDays.has(day);
              const dayPlan = normalizeDayPlan(weeklyPlan[day]);

              return (
                <article className="planner-card" key={day}>
                  <p className="menu-day">{day}</p>
                  {WEEKLY_MEAL_SLOTS.map((slot) => (
                    <div key={`${day}-${slot.id}`}>
                      <label
                        className="planner-label"
                        htmlFor={`dish-${day.toLowerCase()}-${slot.id}`}
                      >
                        {slot.label}
                      </label>
                      <button
                        type="button"
                        id={`dish-${day.toLowerCase()}-${slot.id}`}
                        className={
                          dayPlan[slot.id]
                            ? "dish-picker-trigger"
                            : "dish-picker-trigger dish-picker-trigger-empty"
                        }
                        onClick={() => openDishPicker(day, slot.id)}
                        disabled={isLocked}
                        aria-label={`Selecionar prato de ${slot.label.toLowerCase()} para ${day}`}
                      >
                        {(() => {
                          const selectedDish = getDishById(dayPlan[slot.id]);
                          return selectedDish ? (
                            <>
                              <span className="dish-picker-trigger-title">
                                {selectedDish.name}
                              </span>
                              <span className="dish-picker-trigger-meta">
                                {getDishCategoryLabel(selectedDish)} · {selectedDish.price} EUR
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="dish-picker-trigger-title">Nenhum</span>
                              <span className="dish-picker-trigger-meta">
                                Escolher prato
                              </span>
                            </>
                          );
                        })()}
                      </button>
                    </div>
                  ))}
                  <p className="planner-note">
                    {isLocked
                      ? "Bloqueado porque existem pedidos em aberto para este dia."
                      : "Só os pratos selecionados aparecem na secção de cliente."}
                  </p>
                </article>
              );
            })}
          </div>
        </div>

        <div className="planner-box standalone custom-cards-box">
          <div className="section-heading compact">
            <div>
              <p className="section-kicker">Cartões personalizados</p>
              <h2>Cartões extra de menu</h2>
            </div>
            {!isAddingCustomCard ? (
              <button type="button" className="secondary-button" onClick={addCustomCard}>
                Adicionar cartão
              </button>
            ) : null}
          </div>

          <p className="planner-intro">
            Crie cartões opcionais como Vegetariano, Sem lactose ou Especial do dia.
            Preencha o cartão e confirme para publicar.
          </p>

          {isAddingCustomCard ? (
            <article className="planner-card">
            <label className="planner-label" htmlFor="custom-day-draft">
              Dia da semana
            </label>
            <select
              id="custom-day-draft"
              value={customCardDraft.day}
              onChange={(event) =>
                handleCustomCardDraftChange("day", event.target.value)
              }
            >
              <option value="">Selecionar dia</option>
              {WEEK_DAYS.map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>

            <label className="planner-label" htmlFor="custom-dish-name-draft">
              Nome do prato
            </label>
            <input
              id="custom-dish-name-draft"
              className="planner-input"
              value={customCardDraft.dishName}
              onChange={(event) =>
                handleCustomCardDraftChange("dishName", event.target.value)
              }
              placeholder="Chicken Rice Bowl"
            />

            <label className="planner-label" htmlFor="custom-description-draft">
              Descrição
            </label>
            <input
              id="custom-description-draft"
              className="planner-input"
              value={customCardDraft.description}
              onChange={(event) =>
                handleCustomCardDraftChange("description", event.target.value)
              }
              placeholder="Descreva esta opção especial de menu..."
            />

            <label className="planner-label" htmlFor="custom-image-url-draft">
              URL da imagem (opcional)
            </label>
            <input
              id="custom-image-url-draft"
              className="planner-input"
              value={customCardDraft.imageUrl}
              onChange={(event) =>
                handleCustomCardDraftChange("imageUrl", event.target.value)
              }
              placeholder="https://images.example.com/refeicao.jpg ou link imgres do Google"
            />

            <label className="planner-label" htmlFor="custom-price-draft">
              Preço (EUR)
            </label>
            <input
              id="custom-price-draft"
              type="number"
              min="1"
              step="1"
              className="planner-input"
              value={customCardDraft.price}
              onChange={(event) =>
                handleCustomCardDraftChange("price", event.target.value)
              }
              placeholder="10"
            />

            <label className="planner-toggle">
              <input
                type="checkbox"
                checked={customCardDraft.lactoseFree}
                onChange={(event) =>
                  handleCustomCardDraftChange("lactoseFree", event.target.checked)
                }
              />
              🍶❌ Sem lactose
            </label>

            <label className="planner-toggle">
              <input
                type="checkbox"
                checked={customCardDraft.glutenFree}
                onChange={(event) =>
                  handleCustomCardDraftChange("glutenFree", event.target.checked)
                }
              />
              🌽❌ Sem glúten
            </label>

            <label className="planner-toggle">
              <input
                type="checkbox"
                checked={customCardDraft.vegan}
                onChange={(event) =>
                  handleCustomCardDraftChange("vegan", event.target.checked)
                }
              />
              🌿 Vegano
            </label>

            <label className="planner-toggle">
              <input
                type="checkbox"
                checked={customCardDraft.enabled}
                onChange={(event) =>
                  handleCustomCardDraftChange("enabled", event.target.checked)
                }
              />
              Visível para os clientes
            </label>

            <div className="form-actions">
              <button
                type="button"
                className="primary-button"
                onClick={confirmAddCustomCard}
              >
                {editingCustomCardId ? "Guardar alterações" : "Confirmar cartão"}
              </button>
              <button
                type="button"
                className="secondary-button"
                onClick={cancelAddCustomCard}
              >
                Cancelar
              </button>
            </div>
            </article>
          ) : null}

          {customCards.length > 0 ? (
            <div className="planner-grid">
              {customCards.map((card) => {
                return (
                  <article className="planner-card" key={card.id}>
                    <div className="menu-card-top">
                      <p className="menu-day">{card.day}</p>
                      <span className="price-badge">{card.price} EUR</span>
                    </div>
                    <label className="planner-label"></label> 
                    <p className="planner-dish-text">{card.dishName || "Prato desconhecido"}</p>
                    <p className="planner-note">{card.description}</p>
                    <div className="custom-card-actions">
                      {pendingCustomCardDeleteId === card.id ? (
                        <>
                          <button
                            type="button"
                            className="danger-button card-delete-button"
                            onClick={() => deleteCustomCard(card.id)}
                          >
                            Confirmar
                          </button>
                          <button
                            type="button"
                            className="secondary-button secondary-button-small"
                            onClick={() => setPendingCustomCardDeleteId(null)}
                          >
                            Cancelar
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="secondary-button card-delete-button"
                            onClick={() => editCustomCard(card)}
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            className="danger-button card-delete-button"
                            onClick={() => setPendingCustomCardDeleteId(card.id)}
                          >
                            Eliminar
                          </button>
                        </>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );

  const renderAdminDashboard = () => (
    <>
      <div className="section-heading">
        <div>
          <p className="section-kicker">Cozinha e entregas</p>
          <h2>Painel de administração</h2>
        </div>
        <span className="muted-pill">Administrador confirma entrega e pagamento</span>
      </div>

      <div className="stats-grid">
        <article className="stat-card outside-orders-card">
          <span>Pedidos fora do site</span>
          <div className="outside-orders-control">
            <button
              type="button"
              className="secondary-button secondary-button-small outside-orders-step"
              onClick={() => updateOutsideOrderCount(outsideOrdersTargetCount - 1)}
              disabled={outsideOrdersTargetCount <= 0}
              aria-label="Diminuir pedidos fora do site"
            >
              -
            </button>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={outsideOrdersTargetCount}
              onChange={(event) => updateOutsideOrderCount(event.currentTarget.value)}
              aria-label="Pedidos fora do site"
            />
            <button
              type="button"
              className="secondary-button secondary-button-small outside-orders-step"
              onClick={() => updateOutsideOrderCount(outsideOrdersTargetCount + 1)}
              disabled={outsideOrdersTargetCount >= DAILY_ORDER_LIMIT}
              aria-label="Aumentar pedidos fora do site"
            >
              +
            </button>
          </div>
          <small>Para {orderTarget.label}</small>
        </article>
        <article className="stat-card">
          <span>Pedidos em curso</span>
          <strong>{dashboardData.activeOrders.length}</strong>
        </article>
        <article className="stat-card">
          <span>Aguardando pagamentos</span>
          <strong>{dashboardData.paymentPendingOrders.length}</strong>
        </article>
        <article className="stat-card">
          <span>Concluídos hoje</span>
          <strong>{dashboardData.completedOrders.length}</strong>
        </article>
      </div>

      <div className="dashboard-section">
        <h3>Pedidos em aberto por menu selecionado</h3>
        <div className="summary-list">
          {dashboardData.summary.map((item) => (
            <article className="summary-card" key={item.id}>
              <div className="summary-main">
                <p className="menu-day">{item.day}</p>
                <h4>{item.name}</h4>
              </div>
              <div className="summary-numbers">
                <span>{item.totalQuantity} refeições</span>
                <span>{item.orderCount} pedidos em aberto</span>
              </div>
            </article>
          ))}
        </div>
      </div>
    </>
  );

  const renderAdminLogin = () => (
    <section className="auth-wrapper">
      <div className="section-heading compact">
        <div>
          <p className="section-kicker">Controlo de acesso</p>
          <h2>Iniciar sessão de administrador</h2>
        </div>
        <span className="muted-pill">Obrigatório para painel e planeador</span>
      </div>

      <p className="auth-copy">
        Inicie sessão como administrador para aceder às páginas protegidas.
      </p>

      <form className="order-form auth-form" onSubmit={handleAdminLogin}>
        <label>
          Utilizador
          <input
            name="username"
            placeholder=""
            value={loginForm.username}
            onChange={handleLoginChange}
            required
          />
        </label>

        <label>
          Palavra-passe
          <input
            type="password"
            name="password"
            value={loginForm.password}
            onChange={handleLoginChange}
            required
          />
        </label>

        <div className="form-actions">
          <button type="submit" className="primary-button">
            Iniciar sessão
          </button>
          <button type="button" className="secondary-button" onClick={() => resetView("menu") }>
            Voltar à vista de cliente
          </button>
        </div>
      </form>

      {loginError ? <p className="error-text">{loginError}</p> : null}
    </section>
  );

  const hasDeliveryPending = dashboardData.activeOrders.some(
    (order) => !order.isDelivered
  );
  const hasPaymentPending = dashboardData.activeOrders.some(
    (order) => order.isDelivered && !order.isPaid
  );
  const deliveryCounter = hasDeliveryPending
    ? {
        label: "Entregas por concluir",
        className: "delivery-counter-pill counter-delivery",
      }
    : hasPaymentPending
      ? {
          label: "Aguardando pagamentos",
          className: "delivery-counter-pill counter-payment",
        }
      : {
          label: "Todas as entregas realizadas",
          className: "delivery-counter-pill counter-completed",
        };

  const renderSidePanel = () => (
    <>
      <div className="section-heading compact">
        <div>
          <p className="section-kicker">Ações de entrega</p>
          <h2>Pedidos a processar</h2>
        </div>
        <span className={deliveryCounter.className}>{deliveryCounter.label}</span>
      </div>

      {dashboardData.activeOrders.length === 0 ? (
        <div className="empty-state">
          <p>Não existem pedidos ativos neste momento.</p>
          <span>Novos pedidos de clientes aparecem aqui até a entrega e o pagamento serem confirmados.</span>
        </div>
      ) : (
        <div className="orders-list">
          {dashboardData.activeOrders.map((order) => {
            const status = getOrderStatus(order);
            const isDeletingActiveOrderPending =
              pendingAction?.orderId === order.id &&
              pendingAction?.scope === "admin-active";

            return (
              <article className="order-card" key={order.id}>
                <div className="order-card-body">
                  <div className="order-card-main">
                    <div className="order-card-main-top">
                      <div className="order-card-top">
                        <h3>{order.customerName}</h3>
                        <p>
                          {order.day} • {order.menuName}
                        </p>
                      </div>
                      <div className="order-card-indicators">
                        <span className="quantity-pill">x{order.quantity}</span>
                        {/* <div className={`status-pill status-${status.tone}`}>
                          <span className="status-dot" aria-hidden="true" />
                          <span>{status.label}</span>
                        </div> */}
                      </div>
                    </div>

                    <p>{order.address}</p>
                    <p>{order.phone}</p>
                  </div>
                  <div className="order-note-box">
                    <p>{order.extraNotes || "Sem pedidos extra, observações ou alergias."}</p>
                  </div>

                  <div className="order-card-actions">
                    {!isDeletingActiveOrderPending && status.actionType ? (
                      <button
                        type="button"
                        className={`status-action status-action-${status.tone}`}
                        onClick={() => updateOrderStep(order.id, status.actionType)}
                      >
                        {status.actionLabel}
                      </button>
                    ) : null}
                    {!isDeletingActiveOrderPending && order.isDelivered && !order.isPaid ? (
                      <button
                        type="button"
                        className="secondary-button"
                        onClick={() => updateOrderStep(order.id, "undo-delivery")}
                      >
                        Anular entrega
                      </button>
                    ) : null}
                    {!order.isDelivered &&
                      renderDangerAction({
                        order,
                        scope: "admin-active",
                        buttonLabel: "Eliminar pedido",
                        confirmLabel: "Eliminar pedido ativo?",
                      })}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <div className="completed-box">
        <div className="section-heading compact">
          <div>
            <p className="section-kicker">Caixa verde</p>
            <h2>Entregas concluídas hoje</h2>
          </div>
          <div className="completed-counters">
            <span className="success-pill">{dashboardData.completedMeals} Refeições</span>
            <span className="success-pill">{dashboardData.completedBalance} EUR</span>
          </div>
        </div>

        {dashboardData.completedOrders.length === 0 ? (
          <div className="empty-state empty-state-success">
            <p>Ainda não há nada concluído.</p>
            <span>Os pedidos passam para aqui depois de o administrador confirmar entrega e pagamento.</span>
          </div>
        ) : (
          <div className="completed-list">
            {dashboardData.completedOrders.map((order) => {
              const isDeleteCompletedPending =
                pendingAction?.orderId === order.id &&
                pendingAction?.scope === "admin-completed";
              const isReopenCompletedPending =
                pendingAction?.orderId === order.id &&
                pendingAction?.scope === "admin-completed-reopen";

              return (
                <article className="completed-card" key={order.id}>
                  <div>
                    <p className="menu-day">{order.day}</p>
                    <h3>{order.customerName}</h3>
                  </div>
                  <div className="completed-meta">
                    <span>{order.menuName}</span>
                    <span>x{order.quantity}</span>
                  </div>
                  <div className="customer-order-actions">
                    {!isDeleteCompletedPending &&
                      renderDangerAction({
                        order,
                        scope: "admin-completed-reopen",
                        buttonLabel: "Retroceder",
                        confirmLabel: "Mover este pedido de volta para os pedidos em aberto?",
                        buttonClassName: "secondary-button",
                        confirmButtonClassName: "confirm-button-warning",
                        confirmBoxClassName: "confirm-box-warning",
                        inlineConfirm: true,
                      })}
                    {!isReopenCompletedPending &&
                      renderDangerAction({
                        order,
                        scope: "admin-completed",
                        buttonLabel: "Eliminar pedido",
                        confirmLabel: "Eliminar este pedido concluído? Esta ação não pode ser desfeita.",
                        inlineConfirm: true,
                      })}
                  </div>
                </article>
              );
            })}
          </div>
        )}

      </div>

      <section className="panel warning-box">
        <div className="section-heading compact">
          <div>
            <p className="section-kicker">Avisos</p>
            <h2>Aviso para clientes</h2>
          </div>
          <span className={customerWarning.enabled ? "danger-status-pill" : "muted-pill"}>
            {customerWarning.enabled ? "Publicado" : "Inativo"}
          </span>
        </div>

        <div className="order-form warning-form">
          <label className="warning-toggle">
            <input
              type="checkbox"
              checked={customerWarningDraft.enabled}
              onChange={(event) =>
                handleCustomerWarningDraftChange("enabled", event.target.checked)
              }
            />
            <span>Mostrar aviso na ementa</span>
          </label>

          <label>
            Mensagem
            <textarea
              className="order-notes-input"
              value={customerWarningDraft.message}
              maxLength={180}
              rows={3}
              onChange={(event) =>
                handleCustomerWarningDraftChange("message", event.target.value)
              }
            />
          </label>

          <label>
            Dia do aviso
            <div className="warning-date-selectors">
              <select
                value={warningDateParts.day}
                onChange={(event) =>
                  handleCustomerWarningDatePartChange("day", event.target.value)
                }
              >
                <option value="">Dia</option>
                {warningDayOptions.map((day) => (
                  <option key={day} value={day}>
                    {day}
                  </option>
                ))}
              </select>
              <select
                value={warningDateParts.month}
                onChange={(event) =>
                  handleCustomerWarningDatePartChange("month", event.target.value)
                }
              >
                <option value="">Mês</option>
                {Array.from({ length: 12 }, (_value, index) => {
                  const month = String(index + 1).padStart(2, "0");
                  return (
                    <option key={month} value={month}>
                      {month}
                    </option>
                  );
                })}
              </select>
              <select
                value={warningDateParts.year}
                onChange={(event) =>
                  handleCustomerWarningDatePartChange("year", event.target.value)
                }
              >
                <option value="">Ano</option>
                {warningYearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </label>

          <div className="customer-warning-notice warning-preview" role="status">
            <p>{customerWarningPreview.message}</p>
            {customerWarningPreview.dateLabel ? (
              <span>{customerWarningPreview.dateLabel}</span>
            ) : null}
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="primary-button"
              onClick={saveCustomerWarning}
            >
              Guardar aviso
            </button>
            <button
              type="button"
              className="danger-button"
              onClick={disableCustomerWarning}
            >
              Desativar aviso
            </button>
          </div>
        </div>
      </section>

      <section className="panel cleaning-box">
        <div className="section-heading compact">
          <div>
            <p className="section-kicker">Limpeza</p>
            <h2>Registo de dados e Limpeza</h2>
          </div>
        </div>
        <div className="cleaning-actions">
          {pendingCleaningAction ? (
            <>
              {pendingCleaningAction === "save-current-state"
                ? renderCleaningActionButton({
                    actionId: "save-current-state",
                    label: "Guardar registo",
                    confirmLabel: "Confirmar download",
                    buttonClassName: "secondary-button",
                    confirmButtonClassName: "success-outline-button",
                    onConfirm: () => {
                      downloadCurrentState();
                      setPendingCleaningAction("");
                    },
                  })
                : pendingCleaningAction === "reset-state"
                  ? renderCleaningActionButton({
                      actionId: "reset-state",
                      label: "Limpeza do registo",
                      confirmLabel: "Confirmar limpeza",
                      buttonClassName: "secondary-button",
                      confirmButtonClassName: "danger-button",
                      onConfirm: resetConcludedOrders,
                    })
                  : renderCleaningActionButton({
                      actionId: "general-reset",
                      label: "Limpeza total",
                      confirmLabel: "Confirmar limpeza total",
                      buttonClassName: "danger-button",
                      confirmButtonClassName: "danger-button",
                      onConfirm: resetAllOrders,
                    })}
              <span className="cleaning-action-placeholder" aria-hidden="true" />
            </>
          ) : (
            <>
              {renderCleaningActionButton({
                actionId: "save-current-state",
                label: "Guardar registo",
                confirmLabel: "Confirmar download",
                buttonClassName: "secondary-button",
                confirmButtonClassName: "success-outline-button",
                onConfirm: () => {
                  downloadCurrentState();
                  setPendingCleaningAction("");
                },
              })}
              {renderCleaningActionButton({
                actionId: "reset-state",
                label: "Limpeza do registo",
                confirmLabel: "Confirmar limpeza",
                buttonClassName: "secondary-button",
                confirmButtonClassName: "danger-button",
                onConfirm: resetConcludedOrders,
              })}
              {renderCleaningActionButton({
                actionId: "general-reset",
                label: "Limpeza total",
                confirmLabel: "Confirmar limpeza total",
                buttonClassName: "danger-button",
                confirmButtonClassName: "danger-button",
                onConfirm: resetAllOrders,
              })}
            </>
          )}
        </div>
      </section>
    </>
  );

  if (isLoading) {
    return (
      <div className="app-shell">
        <div className="panel loading-panel">
          <p>A carregar dados da aplicação...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <header className="hero">
        <div className="hero-content">
          {/* <p className="eyebrow">Entrega de comida caseira</p> */}
          {/* <h1>Alfacinha Capixaba</h1>
          <p className="hero-copy">
            Comida portuguesa com um toque brasileiro. 
          </p> */}
          <img
            className="hero-banner-image"
            src="/brand/website-banner.png"
            alt="Alfacinha Capixaba"
          />
        </div>

    {/* <div className="app-shell">
      <header className="hero">
        <div className="hero-content">
          <p className="eyebrow">Entrega de comida caseira</p>
          <h1>Alfacinha Capixaba</h1>
          <p className="hero-copy">
            Comida portuguesa com um toque brasileiro. 
          </p>
          <p className="hero-copy">
            Refeições a custos acessíveis, confecionadas na hora, com ingredientes frescos e da época. Pedidos feitos na véspera e no próprio dia, entre as 14 e as 11 horas. As entregas são feitas em Lisboa, de segunda a sexta-feira, 
            ao almoço, diretamente no seu local de trabalho e limitadas ao número de refeições disponíveis.
          </p>
          <p className="helper-text">
            Se tiver alguma dúvida, por favor, contacte o nosso serviço de apoio.
          </p>
        </div> */}

        <div className={isAdmin ? "hero-actions hero-actions-admin" : "hero-actions"} aria-label="Alternador de vistas">
          {isAdmin ? (
            <>
              <button
                type="button"
                className={view === "menu" ? "tab-button active" : "tab-button"}
                onClick={() => resetView("menu")}
              >
                Ementa
              </button>
              <button
                type="button"
                className={view === "dishes" ? "tab-button active" : "tab-button"}
                onClick={() => resetView("dishes")}
              >
                Pratos
              </button>
              <button
                type="button"
                className={view === "admin" ? "tab-button active" : "tab-button"}
                onClick={() => resetView("admin")}
              >
                Painel de controlo
              </button>
              <button
                type="button"
                className={view === "planner" ? "tab-button active" : "tab-button"}
                onClick={() => resetView("planner")}
              >
                Planeador de menu
              </button>
              <button
                type="button"
                className={view === "delivery" ? "tab-button active" : "tab-button"}
                onClick={() => resetView("delivery")}
              >
                Entregas
              </button>
              <button
                type="button"
                className={view === "about" ? "tab-button active" : "tab-button"}
                onClick={() => resetView("about")}
              >
                Sobre
              </button>
              <button
                type="button"
                className="tab-button"
                onClick={handleAdminLogout}
              >
                Terminar sessão
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className={view === "menu" ? "tab-button active" : "tab-button"}
                onClick={() => resetView("menu")}
              >
                Ementa
              </button>
              <button
                type="button"
                className={view === "dishes" ? "tab-button active" : "tab-button"}
                onClick={() => resetView("dishes")}
              >
                Pratos
              </button>
              <button
                type="button"
                className={view === "about" ? "tab-button active" : "tab-button"}
                onClick={() => resetView("about")}
              >
                Sobre
              </button>
            </>
          )}
        </div>
      </header>

      {renderDishPicker()}

      {actionError ? <p className="error-text app-error">{actionError}</p> : null}
      {orderConfirmation ? (
        <div className="order-confirmation-notice app-success" role="status">
          <p>{orderConfirmation}</p>
        </div>
      ) : null}

      <main className="content-grid content-grid-customer">
        <section
          className={
            !selectedMenu && view === "menu"
              ? "panel-large customer-main-shell"
              : "panel panel-large"
          }
        >
          {selectedMenu ? (
            <>
              <div className="section-heading">
                <div>
                  <p className="section-kicker">
                    {editingOrder ? "Editar pedido" : "Formulário de pedido"}
                  </p>
                  <h2>{selectedMenu.day}</h2>
                </div>
                <span className="price-badge">{selectedMenu.price} EUR</span>
              </div>

              <div className="selected-menu-card">
                <h3>{selectedMenu.name}</h3>
                {selectedMenu.description ? <p>{selectedMenu.description}</p> : null}
              </div>

              {!editingOrder &&
              (isCustomerOrderingClosed || (!isAdmin && isDailyOrderLimitReached)) ? (
                <div className="order-cutoff-notice" role="status">
                  <p>
                    {isDailyOrderLimitReached
                      ? `Limite de pedidos para ${orderTarget.label} atingido.`
                      : orderTarget.closedTitle}
                  </p>
                  <span>
                    {isDailyOrderLimitReached
                      ? `Já não existem pedidos disponíveis para ${orderTarget.label}.`
                      : orderTarget.closedDescription}
                  </span>
                </div>
              ) : null}

              <form className="order-form" onSubmit={handleSubmit}>
                <label>
                  Nome do cliente
                  <input
                    name="customerName"
                    placeholder="Nome para entrega"
                    value={formData.customerName}
                    onChange={handleChange}
                    required
                  />
                </label>

                <label>
                  Número de telefone
                  <input
                    name="phone"
                    placeholder="9XX XXX XXX"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </label>

                <label>
                  Morada de entrega
                  <input
                    name="address"
                    placeholder="Morada de casa ou escritório"
                    value={formData.address}
                    onChange={handleChange}
                    required
                  />
                </label>

                <label>
                  Quantidade
                  <input
                    type="number"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleQuantityChange}
                    inputMode="numeric"
                    onInvalid={(event) => {
                      const input = event.currentTarget;
                      const quantity = Number(input.value);
                      if (
                        Number.isFinite(orderFormQuantityLimit) &&
                        quantity > orderFormQuantityLimit
                      ) {
                        input.setCustomValidity(
                          `Só pode pedir até ${orderFormQuantityLimit} refeições.`
                        );
                      } else if (quantity < 1) {
                        input.setCustomValidity("A quantidade mínima é 1.");
                      }
                    }}
                    required
                  />
                </label>

                <label>
                  Pedidos extra, observações ou alergias
                  <span className="input-char-counter">
                    {`${formData.extraNotes.length}/${EXTRA_NOTES_MAX_LENGTH}`}
                  </span>
                  <textarea
                    name="extraNotes"
                    className="order-notes-input"
                    placeholder="Ex: sem cebola, alergia a frutos secos, pedir talheres..."
                    value={formData.extraNotes}
                    onChange={handleChange}
                    maxLength={EXTRA_NOTES_MAX_LENGTH}
                    rows={4}
                  />
                </label>

                <div className="form-actions">
                  <button
                    type="submit"
                    className="primary-button"
                    disabled={!editingOrder && isCustomerOrderingUnavailable}
                  >
                    {editingOrder
                      ? "Guardar alterações"
                      : isCustomerOrderingUnavailable
                        ? "Pedidos indisponíveis"
                        : "Fazer pedido"}
                  </button>
                  <button type="button" className="secondary-button" onClick={cancelOrder}>
                    Cancelar
                  </button>
                </div>
              </form>
            </>
          ) : view === "menu" ? (
            renderCustomerView()
          ) : view === "dishes" ? (
            renderFeaturedDishesView()
          ) : view === "planner" ? (
            renderMenuPlanner()
          ) : view === "delivery" ? (
            renderSidePanel()
          ) : view === "about" ? (
            renderAboutView()
          ) : view === "login" ? (
            renderAdminLogin()
          ) : (
            renderAdminDashboard()
          )}
        </section>
      </main>
    </div>
  );
}

export default App;
